import { zodResolver } from '@hookform/resolvers/zod';
import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

export const PercentageSectionFormSchema = z.object({
  amount: z
    .number({
      error: 'Amount must be a positive number',
    })
    .positive()
    .max(100, 'Percentage cannot exceed 100'),
});

export type PercentageSectionFormInputs = z.infer<
  typeof PercentageSectionFormSchema
>;

interface PercentageSectionEditableProps {
  defaultValues: PercentageSectionFormInputs;
  isEditable?: boolean;
  startAdornment?: string;
  onUpdateModifier?: (changes: PercentageSectionFormInputs) => void;
}

export const PercentageSection: React.FC<{
  value: number;
  prefix?: string;
}> = ({ prefix = '', value }) => (
  <Typography variant="subtitle1">{`${prefix}${value}%`}</Typography>
);

const PercentageSectionEditable: React.FC<PercentageSectionEditableProps> = ({
  defaultValues,
  isEditable,
  startAdornment,
  onUpdateModifier,
}) => {
  const { control, handleSubmit } = useForm<PercentageSectionFormInputs>({
    defaultValues,
    reValidateMode: 'onBlur',
    resolver: zodResolver(PercentageSectionFormSchema),
  });

  const startAdornmentIcon = (
    <InputAdornment position="start" sx={{ width: 12 }}>
      <Typography variant="body2" color="textPrimary">
        {startAdornment || '%'}
      </Typography>
    </InputAdornment>
  );

  return (
    <Box
      component={'form'}
      noValidate
      autoComplete="off"
      onChange={onUpdateModifier ? handleSubmit(onUpdateModifier) : undefined}
      sx={{ display: 'flex', gap: 2 }}
    >
      {isEditable ? (
        <Controller
          name={'amount'}
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              type="number"
              error={!!fieldState.error}
              helperText={
                fieldState.error?.message ||
                "This will be calculated as a percentage of the selected option's total price."
              }
              slotProps={{
                input: {
                  startAdornment: startAdornmentIcon,
                  inputMode: 'numeric',
                  inputProps: { min: 0 },
                },
              }}
              {...field}
              onChange={(e) => {
                const value = Math.abs(parseFloat(e.target.value));
                const newValue = Number.isNaN(value) ? '' : value;
                e.target.value = newValue.toString();
                field.onChange(+newValue);
              }}
            />
          )}
        />
      ) : (
        <PercentageSection
          prefix={startAdornment}
          value={defaultValues.amount}
        />
      )}
    </Box>
  );
};

export default PercentageSectionEditable;
